import { clamp, isFinite, orderBy } from 'lodash';

import {
  type AtlasGqlHorizonDamage,
  type AtlasGqlHorizonDamagePropagation,
  AtlasGqlPropagationType,
} from 'types/atlas-graphql';
import { type HorizonTheme } from 'utils/color/horizonTheme';

import { ColorCodingAttribute } from './BladeDamagePlotContainer';
import { type PointProps, Circle, Diamond, POINT_SHAPES, Square, Triangle } from './pointShapes';

export type ShapeAndColor = {
  ShapeComponent: React.FunctionComponent<PointProps>;
  color: string;
};
export type ColorCodingMap = { [key: string]: { symbol: ShapeAndColor; damageCount?: number } };

export const NullShapeComponent = Diamond;

export const getSeverityColorCodingMap = (theme: HorizonTheme): ColorCodingMap => ({
  '5': { symbol: { ShapeComponent: Circle, color: theme.red } },
  '4': { symbol: { ShapeComponent: Square, color: theme.orange } },
  '3': { symbol: { ShapeComponent: Triangle, color: theme.yellow } },
  '2': { symbol: { ShapeComponent: Triangle, color: theme.green } },
  '1': { symbol: { ShapeComponent: Triangle, color: theme.darkGreen } },
});
const getShapeAndColorForSeverity = (theme: HorizonTheme, severity?: number): ShapeAndColor => {
  if (!severity) {
    return {
      ShapeComponent: NullShapeComponent,
      color: theme.plot.defaultPointColor,
    };
  }

  const severityColorCodingMap = getSeverityColorCodingMap(theme);
  if (severity >= 5) {
    return severityColorCodingMap['5'].symbol;
  }
  if (severity >= 4) {
    return severityColorCodingMap['4'].symbol;
  }
  if (severity >= 3) {
    return severityColorCodingMap['3'].symbol;
  }
  if (severity >= 2) {
    return severityColorCodingMap['2'].symbol;
  }
  return severityColorCodingMap['1'].symbol;
};

export const getPropagationTypeColorCodingMap = (theme: HorizonTheme): ColorCodingMap =>
  Object.fromEntries(
    Object.values(AtlasGqlPropagationType)
      .filter(type => !type.includes('Progression'))
      .map((type, index) => [
        type,
        {
          symbol: {
            ShapeComponent: POINT_SHAPES[index % POINT_SHAPES.length],
            color: theme.plot.pointColors[index % theme.plot.pointColors.length],
          },
        },
      ])
  );

export const getColorCodingMap = (
  theme: HorizonTheme,
  colorCodingAttributeValues: string[]
): ColorCodingMap => {
  let colorIndex = -1;
  let shapeIndex = -1;

  return orderBy(colorCodingAttributeValues, [0]).reduce<ColorCodingMap>((acc, value) => {
    if (!(String(value) in acc)) {
      colorIndex += 1;
      shapeIndex += 1;

      return {
        ...acc,
        [String(value)]: {
          symbol: {
            ShapeComponent: POINT_SHAPES[shapeIndex % POINT_SHAPES.length],
            color: theme.plot.pointColors[colorIndex % theme.plot.pointColors.length],
          },
        },
      };
    }
    return acc;
  }, {});
};

/**
 * Given x/y coordinates, damage attributes, and optionally an attribute to color code
 * by with a mapping, returns an svg element representing the damage
 */
const getPointComponent = ({
  key,
  x,
  y,
  colorCodingAttribute,
  colorCodingMap,
  damage,
  propagations,
  selectedDamageIds,
  theme,
}: {
  key: number; // React key
  x: number;
  y: number;
  colorCodingAttribute?: ColorCodingAttribute;
  colorCodingMap?: ColorCodingMap;
  damage: AtlasGqlHorizonDamage;
  propagations: AtlasGqlHorizonDamagePropagation[];
  selectedDamageIds?: string[];
  theme: HorizonTheme;
}): JSX.Element => {
  const { id: damageId, primaryObservationGroupAttrs: attributes } = damage;

  const opacity = selectedDamageIds?.length
    ? selectedDamageIds.includes(damageId)
      ? theme.plot.opacity.more
      : theme.plot.opacity.less
    : theme.plot.opacity.default;
  const pointProps = {
    key,
    damageId,
    x,
    y,
    opacity,
  };
  const NullSymbol = {
    ShapeComponent: NullShapeComponent,
    color: theme.plot.defaultPointColor,
  };

  if (colorCodingAttribute === ColorCodingAttribute.Severity) {
    const { ShapeComponent, color } = getShapeAndColorForSeverity(theme, attributes['Severity']);
    return <ShapeComponent {...pointProps} color={color} />;
  } else if (colorCodingAttribute === ColorCodingAttribute.PropagationType) {
    const primaryGroupPropagationType = propagations?.find(p => p?.damageId === damageId)?.type;
    const { symbol } =
      !!primaryGroupPropagationType && !!colorCodingMap
        ? colorCodingMap?.[primaryGroupPropagationType]
        : { symbol: NullSymbol };
    return <symbol.ShapeComponent {...pointProps} color={symbol.color} />;
  } else if (!!colorCodingAttribute) {
    const { symbol } = colorCodingMap?.[attributes[colorCodingAttribute]] ?? {
      symbol: NullSymbol,
    };
    return <symbol.ShapeComponent {...pointProps} color={symbol.color} />;
  } else {
    return <Circle {...pointProps} color={theme.plot.defaultPointColor} />;
  }
};

/**
 * Map function for getting points for edge damages: given distance, xScale, yScale, and attribute
 * to color code, returns an SVG shape, color-coded if appropriate, at radial distance (x) and 50%
 * chord (y), to show all damages along a single line in the y axis.
 *
 * Damages without distance will return null.
 */
export const mapEdgeDamagesToPoints =
  ({
    xScale,
    yScale,
    colorCodingAttribute,
    colorCodingMap,
    propagations,
    selectedDamageIds,
    theme,
  }: {
    xScale: d3.ScaleLinear<number, number, never>;
    yScale: d3.ScaleLinear<number, number, never>;
    colorCodingAttribute?: ColorCodingAttribute;
    colorCodingMap?: ColorCodingMap;
    propagations: AtlasGqlHorizonDamagePropagation[];
    selectedDamageIds?: string[];
    theme: HorizonTheme;
  }) =>
  (damage: AtlasGqlHorizonDamage, index: number): JSX.Element | null => {
    const { Distance: distance } = damage.primaryObservationGroupAttrs;
    const clampedDistance = clamp(distance, xScale.domain()[0], xScale.domain()[1]);

    if (isFinite(distance)) {
      return getPointComponent({
        key: index,
        x: xScale(clampedDistance),
        y: yScale(50),
        colorCodingAttribute,
        colorCodingMap,
        damage,
        propagations,
        selectedDamageIds,
        theme,
      });
    }
    return null;
  };

/**
 * Map function for getting points for side damages: given distance, chord, max blade width,
 * blade length, xScale, yScale, and attribute to color code, returns an SVG shape, color-coded
 * if appropriate, at radial distance (x) and chord position (y), projected from the distance
 * and relative blade width at that distance.
 *
 * Damages without distance or chord will return null.
 */
export const mapSideDamagesToPoints =
  ({
    widths,
    maxWidth,
    length,
    xScale,
    yScale,
    colorCodingAttribute,
    colorCodingMap,
    propagations,
    selectedDamageIds,
    theme,
  }: {
    widths: number[];
    maxWidth: number;
    length: number;
    xScale: d3.ScaleLinear<number, number, never>;
    yScale: d3.ScaleLinear<number, number, never>;
    colorCodingAttribute?: ColorCodingAttribute;
    colorCodingMap?: ColorCodingMap;
    propagations: AtlasGqlHorizonDamagePropagation[];
    selectedDamageIds?: string[];
    theme: HorizonTheme;
  }) =>
  (damage: AtlasGqlHorizonDamage, index: number): JSX.Element | null => {
    const { Distance: distance, Chord: chord } = damage.primaryObservationGroupAttrs;

    if (!isFinite(distance)) {
      return null;
    }

    // clamp so out-of-range distances (negative or beyond blade length) use the root/tip width
    const clampedDistance = clamp(distance, xScale.domain()[0], xScale.domain()[1]);

    const widthIndex = clamp(Math.round((100 * clampedDistance) / length), 0, widths.length - 1);
    const widthAtDistance = widths[widthIndex];

    if (!isFinite(chord)) {
      return null;
    }

    const projectedChord = (widthAtDistance / maxWidth) * chord;

    if (!isFinite(projectedChord)) {
      return null;
    }

    return getPointComponent({
      key: index,
      x: xScale(clampedDistance),
      y: yScale(projectedChord),
      colorCodingAttribute,
      colorCodingMap,
      damage,
      propagations,
      selectedDamageIds,
      theme,
    });
  };

/**
 * Given the path of the selection range box ("d" property of svg path),
 * returns the appropriate coordinates and textAnchor (direction) of the label text
 */
export const getLabelParametersFromRangePath = (
  path?: string | null
): {
  x: number;
  y: number;
  textAnchor: 'start' | 'end';
} => {
  const coordinates = path?.match(/-?\d*\.?\d+/g)?.map(Number) ?? [];

  if (coordinates.length < 4) {
    return {
      x: 25,
      y: 15,
      textAnchor: 'start',
    };
  }

  let xMin = Infinity;
  let xMax = -Infinity;
  let yMin = Infinity;

  // Iterate through x/y pairs
  for (let i = 0; i < coordinates.length; i += 2) {
    const x = coordinates[i];
    const y = coordinates[i + 1];

    if (x < xMin) {
      xMin = x;
    }
    if (x > xMax) {
      xMax = x;
    }
    if (y < yMin) {
      yMin = y;
    }
  }

  // If it will fit in the svg, anchor the text at the top-right of the drawn box
  if (xMax < 900) {
    return {
      x: xMax + 8,
      y: yMin,
      textAnchor: 'start',
    };
  }

  // If the box extends too far to the right and left, anchor the text inside the box in the lop-left
  if (xMin < 110) {
    return {
      x: xMin + 8,
      y: yMin + 4,
      textAnchor: 'start',
    };
  }

  // If the box extends too far to the right, but has space to the left, anchor the text at the top-left
  return {
    x: xMin - 4,
    y: yMin,
    textAnchor: 'end',
  };
};
